// Theme (light / dark / follow-system) — the React equivalent of the PHP
// portal's `?theme=` toggle + `tabler-theme.js`. We flip the `dark` class
// on <html> (the class the @noc/ui token CSS keys off) and persist the
// choice in localStorage under "noc.theme". A tiny inline script in
// index.html applies the same class *before* React mounts, so there's no
// light-mode flash on a dark-mode reload.
//
// `theme` is the user's choice ("light" | "dark" | "system"); when it's
// "system" we mirror `prefers-color-scheme` live. `resolvedTheme` is what
// is actually painted — that's what UI (the toggle icon) should read.

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "noc.theme";

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolve(theme: Theme): ResolvedTheme {
  if (theme === "system") return systemPrefersDark() ? "dark" : "light";
  return theme;
}

function readStoredTheme(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* ignore (private mode, etc.) */
  }
  return "system";
}

function applyResolved(r: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle("dark", r === "dark");
  root.style.colorScheme = r;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolve(readStoredTheme()));

  // Apply whenever the user's choice changes.
  useEffect(() => {
    const r = resolve(theme);
    setResolvedTheme(r);
    applyResolved(r);
  }, [theme]);

  // When following the system, react to OS-level changes live.
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const r: ResolvedTheme = mq.matches ? "dark" : "light";
      setResolvedTheme(r);
      applyResolved(r);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  const setTheme = (t: Theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      /* ignore */
    }
    setThemeState(t);
  };

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const v = useContext(ThemeContext);
  if (!v) throw new Error("useTheme must be used within <ThemeProvider>");
  return v;
}
